import { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { setSessionEndedHandler } from '@/api/services/auth.service'
import { larrySessionExpiredMessage } from '@/lib/embed/host-messages'
import { postToParent } from '../handlers/post-to-parent'

/**
 * While Larry is embedded, a force logout (the JWT couldn't be refreshed) must not load Larry's
 * login page inside the host's iframe: the host owns the session. Instead this tells the host with
 * `session-expired`, once, so it can check its own session and sign the user out, and returns true
 * so the page can show that the session ended.
 *
 * It runs before the logout clears auth and the query cache, and unmounts the editor right away
 * (`flushSync`), so home never renders against the emptied cache (WMPCM-12160).
 *
 * Before `isHostSessionStarted()`, a force logout is for a session left in this tab's
 * sessionStorage by an earlier embed (its JWT expired since). The logout clears it and the embed
 * carries on with the host's session; the host isn't told.
 *
 * `targetOrigins` is where the message goes: the host's origin once known, else every allowed one.
 * `isHostSessionStarted` must be stable.
 */
export function useHostSessionEnded(
  targetOrigins: readonly string[],
  isHostSessionStarted: () => boolean
): boolean {
  const [sessionEnded, setSessionEnded] = useState(false)
  const targetOriginsRef = useRef(targetOrigins)

  useEffect(() => {
    targetOriginsRef.current = targetOrigins
  }, [targetOrigins])

  useEffect(() => {
    let notified = false

    setSessionEndedHandler(() => {
      if (!isHostSessionStarted() || notified) return

      notified = true
      flushSync(() => setSessionEnded(true))
      postToParent(larrySessionExpiredMessage(), targetOriginsRef.current)
    })

    return () => setSessionEndedHandler(null)
  }, [isHostSessionStarted])

  return sessionEnded
}
